// @spec AST-STR-057
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Package, Shield } from 'lucide-react';
import { useState } from 'react';
import { Toaster } from 'sonner';

import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AuditorAssetDetail } from '@/features/auditor-access/components/AuditorAssetDetail';
import { AuditorAssetList } from '@/features/auditor-access/components/AuditorAssetList';
import { AuditorModeBanner } from '@/features/auditor-access/components/AuditorModeBanner';
import { useAuditorStore } from '@/stores/auditor-store';

export const Route = createFileRoute('/auditor/access/$accessId/assets')({
  component: AuditorAssetsPage,
});

/**
 * Auditor assets page.
 *
 * Displays a read-only list of assets for an organization
 * that the auditor has access to review.
 */
function AuditorAssetsPage() {
  const { accessId } = Route.useParams();
  const navigate = useNavigate();

  const isAuditorMode = useAuditorStore.use.isAuditorMode();
  const currentAccess = useAuditorStore.use.currentAccess();

  const { t } = useTranslation('auditor-access');
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  // Verify user is in auditor mode and has the correct access ID
  if (!isAuditorMode || !currentAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessRequired')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessRequiredDescription')}
            </AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={() => void navigate({ to: '/' })}>
            {t('routes.goToHome')}
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access ID matches the current auditor mode
  if (currentAccess.accessId !== accessId) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.invalidAccess')}</AlertTitle>
            <AlertDescription>
              {t('routes.invalidAccessDescription')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() =>
              void navigate({ to: `/auditor/access/${currentAccess.accessId}/assets` })
            }
          >
            {t('routes.goToYourAssets')}
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access scope includes 'assets'
  if (!currentAccess.accessScope.includes('assets')) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessDenied')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessDeniedAssets')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => void navigate({ to: '/dashboard' })}
          >
            {t('routes.goToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  const handleAssetClick = (assetId: string) => {
    setSelectedAssetId(assetId);
  };

  const handleBackToList = () => {
    setSelectedAssetId(null);
  };

  const handleControlClick = (controlId: string) => {
    void navigate({ to: `/auditor/access/${accessId}/controls/${controlId}` });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Auditor mode banner */}
      <AuditorModeBanner hasOtherOrganizations={false} />

      <div className="p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          {/* Page header - only shown in list view */}
          {!selectedAssetId && (
            <header className="space-y-1">
              <div className="flex items-center gap-2">
                <Package className="h-6 w-6 text-amber-600" aria-hidden="true" />
                <h1 className="text-2xl font-semibold text-foreground">{t('routes.auditorAssetReview')}</h1>
              </div>
              <p className="text-muted-foreground">
                {t('routes.readOnlyAccessToAssets', { orgName: currentAccess.name })}
              </p>
            </header>
          )}

          {/* Content */}
          {selectedAssetId ? (
            <AuditorAssetDetail
              accessId={accessId}
              assetId={selectedAssetId}
              onBack={handleBackToList}
              onControlClick={handleControlClick}
            />
          ) : (
            <AuditorAssetList
              accessId={accessId}
              organizationName={currentAccess.name}
              onAssetClick={handleAssetClick}
            />
          )}
        </div>
      </div>

      {/* Toast notifications */}
      <Toaster position="top-right" richColors />
    </div>
  );
}
